import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { Observable, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { ButtonType, ModalButtons, NotificationTheme, LoaderTheme } from '@fcom/ui-components';
import { Profile } from '@fcom/core-api/login';
import { FormStatus } from '@fcom/service-forms';
import { trackFormState } from '@fcom/service-forms/services/utils';
import { GtmService } from '@fcom/common/gtm';
import { dateValidator } from '@fcom/service-forms/services/form-validators';
import { LocalDate, unsubscribe } from '@fcom/core/utils';
import { SelectOption } from '@fcom/common';
import { CountryCode } from '@fcom/core';
import { LanguageService } from '@fcom/ui-translate';
import { finShare } from '@fcom/rx';

import { PassportInformationFormFields, PassportInformationSubmissionData } from '../../interfaces/profile.interface';
import { ProfileService } from '../../services/profile.service';

const PASSPORT_FORM = 'update-profile-passport-information';

@Component({
  selector: 'fin-loyalty-passport-information-form',
  templateUrl: 'loyalty-passport-information-form.component.html',
  styleUrls: ['./loyalty-passport-information-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class LoyaltyPassportFormComponent implements OnInit, OnDestroy {
  readonly FormStatus = FormStatus;
  readonly PassportInformationFormFields = PassportInformationFormFields;
  readonly ModalButtons = ModalButtons;
  readonly ButtonType = ButtonType;
  readonly NotificationTheme = NotificationTheme;
  readonly LoaderTheme = LoaderTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  reactiveForm: UntypedFormGroup;
  countries$: Observable<SelectOption[]>;
  formStatus: FormStatus = FormStatus.INITIAL;
  modalOpen = false;

  currentDate = LocalDate.now();
  expiryMinDate = LocalDate.now().minusYears(15);
  expiryMaxDate = LocalDate.now().plusYears(15);

  public notification: ElementRef;

  @ViewChild('fcomNotification', { static: false })
  set notificationElem(notificationElem: ElementRef) {
    this.notification = notificationElem;
  }

  @ViewChild('openModalButton', { static: false })
  openModalButton: ElementRef;

  private subscriptions = new Subscription();

  constructor(
    private profileService: ProfileService,
    private languageService: LanguageService,
    private fb: UntypedFormBuilder,
    private gtmService: GtmService,
    private cdRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.formStatus = FormStatus.LOADING;
    const countryCodes$: Observable<CountryCode[]> = this.languageService.translate('countryCodes').pipe(finShare());

    this.countries$ = countryCodes$.pipe(
      map((countryCodes) => {
        const selectOptions: SelectOption[] = countryCodes.map((countryCodeData) => ({
          value: countryCodeData.countryCode,
          name: countryCodeData.name,
        }));
        selectOptions.unshift({ value: '', name: '-' });
        return selectOptions;
      }),
      take(1)
    );

    this.subscriptions.add(
      this.profileService.getProfile().subscribe((profile: Profile) => {
        this.createForm(profile);
        this.formStatus = FormStatus.INITIAL;
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  createForm(profile: Profile): void {
    this.reactiveForm = this.fb.group({
      [PassportInformationFormFields.NUMBER]: [
        profile.passport.passportNumber ? profile.passport.passportNumber : '',
        Validators.required,
      ],
      [PassportInformationFormFields.ISSUE_DATE]: [
        profile.passport.passportIssueDate ? new LocalDate(profile.passport.passportIssueDate) : '',
        [dateValidator(), Validators.required],
      ],
      [PassportInformationFormFields.EXPIRY_DATE]: [
        profile.passport.passportExpirationDate ? new LocalDate(profile.passport.passportExpirationDate) : '',
        [dateValidator(), Validators.required],
      ],
      [PassportInformationFormFields.COUNTRY]: [
        profile.passport.passportCountry ? profile.passport.passportCountry : '',
        Validators.required,
      ],
    });
  }

  onSubmit(): void {
    if (this.formStatus === FormStatus.LOADING) {
      return;
    }

    if (!this.reactiveForm.valid) {
      this.reactiveForm.markAllAsTouched();

      return;
    }

    this.formStatus = FormStatus.LOADING;

    const data: PassportInformationSubmissionData = {
      travelProfile: {
        passportInformation: {
          passportNumber: this.reactiveForm.value[PassportInformationFormFields.NUMBER],
          passportIssueDate: this.reactiveForm.value[PassportInformationFormFields.ISSUE_DATE]?.toString(),
          passportExpirationDate: this.reactiveForm.value[PassportInformationFormFields.EXPIRY_DATE]?.toString(),
          passportCountry: this.reactiveForm.value[PassportInformationFormFields.COUNTRY],
        },
      },
    };

    trackFormState(PASSPORT_FORM, 'send-attempt', this.gtmService);

    this.subscriptions.add(
      this.profileService.updatePassportInformation(data).subscribe({
        next: (result) => {
          if (result.status.message === 'OK') {
            this.formStatus = FormStatus.COMPLETE;
            trackFormState(PASSPORT_FORM, 'success', this.gtmService);
          } else {
            this.formStatus = FormStatus.ERROR;
            trackFormState(PASSPORT_FORM, 'error', this.gtmService);
          }
        },
        error: () => {
          this.formStatus = FormStatus.ERROR;
          trackFormState(PASSPORT_FORM, 'error', this.gtmService);
        },
        complete: () => {
          this.cdRef.detectChanges();
          this.notification.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        },
      })
    );
  }

  openModal(event: MouseEvent): void {
    event.stopPropagation();
    this.modalOpen = true;
  }

  closeModal(): void {
    this.openModalButton.nativeElement.focus();
  }
}
